/**
 * 🌟 USE SOUL - Hook pour Soul, Psyche et Compatibilité
 * Architecture Mozart/Jobs/Tesla
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useSoul = (customerId) => {
  // État
  const [isLoading, setIsLoading] = useState(false);
  const [aspirationsOptions, setAspirationsOptions] = useState(null);
  const [myAspirations, setMyAspirations] = useState(null);
  const [psycheQuestions, setPsycheQuestions] = useState([]);
  const [psycheProfile, setPsycheProfile] = useState(null);
  const [compatibility, setCompatibility] = useState(null);

  // ============================================
  // SOUL ASPIRATIONS
  // ============================================
  const loadAspirationsOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/soul/aspirations/options`);
      const data = await response.json();
      
      if (data.success) {
        setAspirationsOptions({
          dreams: data.dreams || [],
          values: data.values || [],
          growth: data.growth || []
        });
        return data;
      }
    } catch (err) {
      console.error('useSoul loadAspirationsOptions error:', err);
      toast.error('Erreur de chargement des options');
    } finally {
      setIsLoading(false);
    }
    return null;
  }, []);

  const loadMyAspirations = useCallback(async () => {
    if (!customerId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/soul/aspirations/${customerId}`);
      const data = await response.json();
      
      if (data.success && data.aspirations) {
        setMyAspirations(data.aspirations);
        return data.aspirations;
      }
    } catch (err) {
      console.error('useSoul loadMyAspirations error:', err);
    }
    return null;
  }, [customerId]);

  const saveAspirations = useCallback(async (aspirations) => {
    if (!customerId) return { success: false };
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/soul/aspirations/${customerId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(aspirations)
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setMyAspirations(aspirations);
        toast.success('🌟 Aspirations enregistrées !');
        return { success: true };
      }
    } catch (err) {
      console.error('useSoul saveAspirations error:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsLoading(false);
    }
    return { success: false };
  }, [customerId]);

  // ============================================
  // PSYCHE QUIZ
  // ============================================
  const loadPsycheQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/psyche/quiz/questions`);
      const data = await response.json();
      
      if (data.success && data.questions) {
        setPsycheQuestions(data.questions);
        return data.questions;
      }
    } catch (err) {
      console.error('useSoul loadPsycheQuestions error:', err);
      toast.error('Erreur de chargement des questions');
    } finally {
      setIsLoading(false);
    }
    return [];
  }, []);

  const submitPsycheQuiz = useCallback(async (answers) => {
    if (!customerId) return { success: false };
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/psyche/quiz/submit/${customerId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers })
        }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success('🌟 Merci pour tes réponses !');
        return { success: true };
      }
    } catch (err) {
      console.error('useSoul submitPsycheQuiz error:', err);
      toast.error('Erreur lors de la soumission');
    } finally {
      setIsLoading(false);
    }
    return { success: false };
  }, [customerId]);

  const loadPsycheProfile = useCallback(async () => {
    if (!customerId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/psyche/profile/${customerId}`);
      const data = await response.json();
      
      if (data.success && data.profile) {
        setPsycheProfile(data.profile);
        return data.profile;
      }
    } catch (err) {
      console.error('useSoul loadPsycheProfile error:', err);
    }
    return null;
  }, [customerId]);

  // ============================================
  // COMPATIBILITY
  // ============================================
  const calculateCompatibility = useCallback(async (targetId, detailed = false) => {
    if (!customerId || !targetId) return null;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/compatibility/${customerId}/${targetId}?detailed=${detailed}`
      );
      const data = await response.json();
      
      if (data.success) {
        const result = {
          globalScore: data.globalScore,
          level: data.level,
          emoji: data.emoji,
          message: data.message,
          breakdown: data.breakdown
        };
        setCompatibility(result);
        return result;
      }
    } catch (err) {
      console.error('useSoul calculateCompatibility error:', err);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [customerId]);

  const getCompatibilityExplanation = useCallback(async (targetId) => {
    if (!customerId || !targetId) return null;
    
    try {
      const response = await fetch(
        `${API_URL}/api/compatibility/explain/${customerId}/${targetId}`
      );
      const data = await response.json();
      
      if (data.success) {
        return {
          explanation: data.explanation,
          highlights: data.highlights,
          tips: data.tips
        };
      }
    } catch (err) {
      console.error('useSoul getCompatibilityExplanation error:', err);
    }
    return null;
  }, [customerId]);

  // ============================================
  // QUIZ STATUS
  // ============================================
  const getQuizStatus = useCallback(async () => {
    if (!customerId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/psyche/quiz/status/${customerId}`);
      const data = await response.json();
      
      if (data.success) {
        return {
          hasCompletedSoul: data.hasCompletedSoul,
          hasCompletedPsyche: data.hasCompletedPsyche,
          soulMessage: data.soulMessage,
          psycheMessage: data.psycheMessage
        };
      }
    } catch (err) {
      console.error('useSoul getQuizStatus error:', err);
    }
    return null;
  }, [customerId]);

  // ============================================
  // RESET
  // ============================================
  const clearCompatibility = useCallback(() => {
    setCompatibility(null);
  }, []);

  return {
    // État
    isLoading,
    aspirationsOptions,
    myAspirations,
    psycheQuestions,
    psycheProfile,
    compatibility,
    
    // Soul Actions
    loadAspirationsOptions,
    loadMyAspirations,
    saveAspirations,
    
    // Psyche Actions
    loadPsycheQuestions,
    submitPsycheQuiz,
    loadPsycheProfile,
    
    // Compatibility Actions
    calculateCompatibility,
    getCompatibilityExplanation,
    clearCompatibility,
    
    // Status
    getQuizStatus
  };
};

export default useSoul;
